import React, { useState } from 'react';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import WarningModal from '../../../components/WarningModal';
import MyAppText from '../../../components/MyAppText';
import { NavigationProp, useIsFocused, useNavigation } from '@react-navigation/native';
import { translate } from '../../../services/translate';
import { DocumentsParamList } from '../../../typings/Params';
import { documentService } from '../../../services/central-api/document';
import DataTableContainer from '../../../components/DataTableContainer';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import DropdownButton from '../../../components/DropdownButton';
import { TermsOfUseAndPrivacyVersionListItem } from '../../../services/central-api/document';
import { downloadTermsVersion } from '../DownloadTermsVersion';

interface Props {
    status: 'scheduled' | 'previous';
}

export default function TermsVersionsGuardianList({ status }: Props) {
    const navigation = useNavigation<NavigationProp<DocumentsParamList>>();
    const isFocused = useIsFocused();

    const [filters, setFilters] = useState({ page: 0, limit: 25 });

    const [pendingAction, setPendingAction] = useState<{ type: 'publish' | 'delete'; row: TermsOfUseAndPrivacyVersionListItem; }>();
    const [confirmVisible, setConfirmVisible] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['getAllTermsOfUseAndPrivacyVersions', 'guardian_app', status, filters],
        queryFn: () => documentService.getAllTermsOfUseAndPrivacyVersions({ ...filters, status, application: 'guardian_app' }),
        enabled: isFocused,
    });

    function getDocumentName(row: TermsOfUseAndPrivacyVersionListItem) {
        const { documentType, language } = row.termsOfUseAndPrivacy;
        return `${translate(documentType)} (${row.version}) - ${language.toUpperCase()}`;
    }

    function getConfirmText() {
        if (!pendingAction) {
            return '';
        }

        const { type, row } = pendingAction;
        const name = getDocumentName(row);

        if (type === 'publish') {
            return translate('publishTermsNowText', {
                name,
                documentType: translate(row.termsOfUseAndPrivacy.documentType),
            });
        }

        return translate('deleteTermsText', {
            name,
            date: moment(row.effectiveDate).format('DD/MM/YYYY'),
        });
    }

    function askConfirmation(type: 'publish' | 'delete', row: TermsOfUseAndPrivacyVersionListItem) {
        setPendingAction({ type, row });
        setConfirmVisible(true);
    }

    async function confirmPendingAction() {
        setConfirmVisible(false);

        if (!pendingAction) {
            return;
        }

        const { type, row } = pendingAction;

        try {
            if (type === 'publish') {
                await documentService.publishTermsOfUseAndPrivacyVersion(row.termsOfUseAndPrivacy.id, row.version);
            } else {
                await documentService.deleteTermsOfUseAndPrivacyVersion(row.termsOfUseAndPrivacy.id, row.version);
            }

            Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
            setTimeout(() => window.location.reload(), 1000);
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            }
            console.error(err);
            Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
        } finally {
            setPendingAction(undefined);
        }
    }

    function getActions(row: TermsOfUseAndPrivacyVersionListItem) {
        const id = row.termsOfUseAndPrivacy.id;

        if (status === 'scheduled') {
            return [
                {
                    content: translate('publishNow'),
                    onPress: () => askConfirmation('publish', row),
                },
                {
                    content: translate('editInformation'),
                    onPress: () => {
                        navigation.navigate('TermsAndPrivacyGuardianForm', { id, version: row.version, mode: 'editScheduled' });
                    }
                },
                {
                    content: translate('deleteDocument'),
                    onPress: () => askConfirmation('delete', row),
                },
            ];
        }

        return [
            {
                content: translate('viewDocument'),
                onPress: () => {
                    navigation.navigate('TermsAndPrivacyGuardianForm', { id, version: row.version, mode: 'view' });
                }
            },
            {
                content: translate('downloadDocument'),
                onPress: () => downloadTermsVersion(id, row.version),
            },
        ];
    }

    return (<>
        <DataTableContainer
            dataTable={<MyDataTable
                columns={[
                    {
                        name: translate('documentType'),
                        selector: row => translate(row.termsOfUseAndPrivacy.documentType),
                        wrap: true,
                    },
                    {
                        name: translate('effectiveDate'),
                        selector: row => moment(row.effectiveDate).format('DD/MM/YYYY'),
                        wrap: true,
                        omit: status !== 'previous',
                    },
                    {
                        name: translate('language'),
                        selector: row => translate(row.termsOfUseAndPrivacy.language),
                        wrap: true,
                        omit: status === 'previous',
                    },
                    {
                        name: translate('version'),
                        selector: row => row.version,
                    },
                    {
                        name: translate('effectiveDate'),
                        selector: row => moment(row.effectiveDate).format('DD/MM/YYYY'),
                        wrap: true,
                        omit: status === 'previous',
                    },
                    {
                        cell: row => <DropdownButton actions={getActions(row)} />,
                        wrap: true,
                        button: true,
                    }
                ]}
                data={data?.rows ?? []}
                progressPending={isLoading}
            />}
            pagination={<Pagination
                totalItems={data?.count ?? 0}
                currentPage={filters.page}
                pageSize={filters.limit}
                setPageSize={pageSize => {
                    setFilters({ limit: pageSize, page: 0 });
                }}
                setPage={page => {
                    setFilters(old => ({ ...old, page }));
                }}
            />}
        />

        <WarningModal
            isModalVisible={confirmVisible}
            setIsModalVisible={setConfirmVisible}
            text={<>
                <MyAppText style={{ fontWeight: 'bold' }}>{translate(pendingAction?.type === 'publish' ? 'publishTermsNowTitle' : 'deleteTermsTitle')}</MyAppText>
                {'\n\n'}
                {getConfirmText()}
            </>}
            confirmType={pendingAction?.type === 'delete' ? 'destructive' : undefined}
            confirmText={pendingAction?.type === 'delete' ? 'yesDelete' : 'publish'}
            cancelText='cancel'
            onSubmit={confirmPendingAction}
            onClose={() => {
                setConfirmVisible(false);
                setPendingAction(undefined);
            }}
        />
    </>);
}
